import ENV from 'scaleflow/config/environment';

/**
 * Seals a GitLab personal access token in the browser so api-prod never sees a
 * credential it could read.
 *
 * The public key lives in `config/environment.js` (`ENV.gitlab`) rather than
 * being written into this module, so a deploy can inject the real one through
 * `GITLAB_CREDENTIAL_PUBLIC_KEY` without a code change and rotation does not
 * mean a source edit. Bundling a *public* key is not a leak — that property is
 * the whole reason an asymmetric key is the right shape here. The private half
 * is a KMS key in the xray-workflow account and only `clone-consumer` holds
 * `kms:Decrypt` for it.
 *
 * See docs/ai-blueprints/gitlab-connector-frontend.md.
 */

/**
 * The envelope that goes over the wire in place of a raw token.
 *
 * `key_id` travels with the ciphertext because asymmetric KMS `Decrypt` cannot
 * infer which key sealed a blob — a ciphertext that arrives without it is
 * unopenable, permanently.
 */
export interface SealedCredential {
  key_id: string;
  ciphertext: string;
}

/**
 * Exactly these three fields, nothing else.
 *
 * There is no username: `oauth2` is a constant that clone-consumer supplies at
 * the git call site, the same way GitHub uses `x-access-token`. There is no
 * timestamp either.
 *
 * `tenant_id` is sealed *inside* the payload rather than only travelling
 * alongside it, because it is the anti-transplant check — clone-consumer
 * verifies the sealed tenant against the job's tenant, so a captured blob
 * cannot be replayed onto another tenant's record.
 */
export interface GitlabCredentialPayload {
  tenant_id: string;
  instance_url: string;
  token: string;
}

/**
 * What `ENV.gitlab.credentialPublicKey` holds until the xray-workflow KMS key
 * is deployed and a real key is injected at build time.
 */
export const PLACEHOLDER_PUBLIC_KEY = 'REPLACE_WITH_KMS_PUBLIC_KEY';

/**
 * Thrown rather than sealing against the placeholder. A blob encrypted with a
 * key nobody holds the private half of is indistinguishable from a good one
 * until clone time, so this fails at the point of use instead.
 */
export class GitlabSealNotConfiguredError extends Error {
  constructor() {
    super(
      'GitLab credential sealing is not configured — ENV.gitlab.credentialPublicKey ' +
        'is still the placeholder. Inject the real key via GITLAB_CREDENTIAL_PUBLIC_KEY: ' +
        'aws kms get-public-key --key-id alias/gitlab-credential-v1 --query PublicKey --output text',
    );
    this.name = 'GitlabSealNotConfiguredError';
  }
}

/**
 * Whether a real public key has been injected. Callers that render a connect
 * affordance should check this first — without a key, submitting the form can
 * only ever fail.
 */
export function isGitlabSealConfigured(): boolean {
  const key = ENV.gitlab?.credentialPublicKey;
  return (
    typeof key === 'string' && key.length > 0 && key !== PLACEHOLDER_PUBLIC_KEY
  );
}

/**
 * The crypto core, taking its key explicitly. `sealGitlabCredential` is the
 * config-bound wrapper around it; this exists separately so a caller (or a
 * test) can seal against a key that is not the ambient configured one.
 */
export async function sealCredential(
  publicKeySpki: string,
  keyId: string,
  payload: GitlabCredentialPayload,
): Promise<SealedCredential> {
  const key = await crypto.subtle.importKey(
    'spki',
    Uint8Array.from(atob(publicKeySpki), (c) => c.charCodeAt(0)),
    { name: 'RSA-OAEP', hash: 'SHA-256' },
    false,
    ['encrypt'],
  );

  const sealed = await crypto.subtle.encrypt(
    { name: 'RSA-OAEP' },
    key,
    new TextEncoder().encode(JSON.stringify(payload)),
  );

  // RSA-4096 ciphertext is 512 bytes, comfortably inside the argument limit a
  // spread hits. RSA-4096 also leaves ~446 bytes of payload room against a
  // ~130-byte payload, which is why there is no hybrid AES layer here.
  return {
    key_id: keyId,
    ciphertext: btoa(String.fromCharCode(...new Uint8Array(sealed))),
  };
}

/**
 * Seals `{ tenant_id, instance_url, token }` with the configured public key.
 *
 * The token is an argument and a local only — it is never assigned to a
 * property, never logged, and never included in a thrown error's message.
 */
export async function sealGitlabCredential(
  tenantId: string,
  instanceUrl: string,
  token: string,
): Promise<SealedCredential> {
  if (!isGitlabSealConfigured()) {
    throw new GitlabSealNotConfiguredError();
  }

  // An empty tenant would seal cleanly and then fail clone-consumer's
  // anti-transplant check minutes later, on a job with no obvious connection to
  // this form submission. Cheaper to refuse here.
  if (!tenantId) {
    throw new Error('Cannot seal a GitLab credential without a tenant id.');
  }

  return sealCredential(
    ENV.gitlab.credentialPublicKey,
    ENV.gitlab.credentialKeyId,
    {
      tenant_id: tenantId,
      instance_url: instanceUrl,
      token,
    },
  );
}
